import { CSSResultGroup, html, TemplateResult } from 'lit';

import { customElement, property, state } from 'lit/decorators.js';

import { ClassInfo, classMap } from 'lit/directives/class-map.js';

import componentStyles from './idb-percentage-card.css.lit';
import { OutlineContentCard } from '../../base/outline-content-card/outline-content-card';
import '../idb-percentage-bar/idb-percentage-bar';

interface Indicator {
  overlap: boolean;
  twoRows: boolean;
  withShadow: boolean;
  showDescription: boolean;
  name: string;
  value: string;
  description: string;
  detail: string;
  progressValue: string;
  showProgress: boolean;
}

/**
 * The IDB percentage card component.
 * @element idb-percentage-card
 */
@customElement('idb-percentage-card')
export class IdbPercentageCard extends OutlineContentCard {
  static styles: CSSResultGroup = [componentStyles];

  @property({ type: Array, attribute: 'indicators' })
  indicators: Indicator[] = [];

  @property({ type: Boolean, attribute: 'is-expanded' })
  isExpanded = false;

  @property({ type: String, attribute: 'init-height' })
  initHeight = '405';

  @property({ type: String, attribute: 'button-text-open' })
  buttonTextOpen = 'SEE MORE';

  @property({ type: String, attribute: 'button-text-close' })
  buttonTextClose = 'SEE LESS';

  @property({ type: Boolean })
  expanded = false;

  @state() hasSubTitleSlot: boolean;

  firstUpdated(): void {
    this.hasTitleSlot = this.slots.test('title');
    this.hasSubTitleSlot = this.slots.test('subtitle');
  }

  updated(changedProperties: Map<string, bigint>) {
    if (changedProperties.has('initHeight')) {
      this.style.setProperty('--initial-height', `${this.initHeight}px`);
    }
  }

  private toggleWrapperHeight(event: MouseEvent) {
    event.preventDefault();

    const wrapperMask = this.shadowRoot?.querySelector(
      '.graph-wrapper'
    ) as HTMLElement;

    const wrapperInner = this.shadowRoot?.querySelector(
      '.graph-inner-wrapper'
    ) as HTMLElement;

    if (this.expanded) {
      wrapperMask.style.height = `${this.initHeight}px`;
    } else {
      const innerHeight = wrapperInner.scrollHeight + 16;
      wrapperMask.style.height = `${innerHeight}px`;
    }

    this.expanded = !this.expanded;
  }

  private toggleButtonIcon(): string {
    return this.expanded ? 'arrow-up' : 'arrow-down';
  }

  private toggleButtonText(): string {
    return this.expanded ? this.buttonTextClose : this.buttonTextOpen;
  }

  render(): TemplateResult {
    const cardClasses = {
      'percentage-card': true,
    };
    const titleClasses = {
      'card-title': true,
    };
    const subtitleClasses = {
      'card-subtitle': true,
    };
    const percentageWrapperClasses = {
      'graph-wrapper': true,
    };
    const percentageFullWrapperClasses = {
      'graph-inner-wrapper': true,
    };

    return html`
      <section class="${classMap(cardClasses)}">
        <header>
          ${this.titleTemplate(titleClasses)}
        </header>
        ${this.subtitleTemplate(subtitleClasses)}
        <div class="card-content">
          <section class="${classMap(percentageWrapperClasses)}">
            <section class="${classMap(percentageFullWrapperClasses)}">
              ${this.indicators.map((current, index) => {
                return html`
                  <idb-percentage-bar
                    ?overlap="${current.overlap || false}"
                    ?two-rows="${current.twoRows || false}"
                    ?with-shadow="${current.withShadow || false}"
                    ?show-description="${current.showDescription || false}"
                    name="${current.name}"
                    value="${current.value}"
                    description="${current.description}"
                    detail="${current.detail}"
                    progress-value="${current.progressValue}"
                    ?show-progress="${current.showProgress && index == 0}"
                  ></idb-percentage-bar>
                `;
              })}
            </section>
          </section>
        </div>
        <footer>
          ${!this.isExpanded ? this.buttonTemplate() : null}
        </footer>
      </section>
    `;
  }

  subtitleTemplate(classes: ClassInfo): TemplateResult {
    return html`
      <section class="${classMap(classes)}">
        ${this.slotsFancy.renderInShadow('subtitle')}
      </section>
    `;
  }

  buttonTemplate(): TemplateResult {
    return html`
      <idb-button
        button-url="#"
        button-size="small"
        icon-right="${this.toggleButtonIcon()}"
        button-variant="secondary"
        button-label="Button"
        @click="${this.toggleWrapperHeight}"
      >
        ${this.toggleButtonText()}
      </idb-button>
    `;
  }
}
